import { type RefObject, useCallback, useEffect, useState } from "react"

import { addMinutes } from "date-fns"

import { layoutOffsetX } from "@/lib/pointerCoords"
import { positionToTime, snapToInterval } from "@/lib/timelineScale"
import type { Route } from "@/types/collections"

import { SNAP_INTERVAL } from "./useStopDrag"

export interface DragCreateState {
  rowIndex: number
  startX: number
  currentX: number
  startTime: Date
  endTime: Date
}

interface DragCreateOptions {
  sortedRoutes: Route[]
  isToday: boolean
  selectedDate: Date
  hourWidth: number
  timelineContentRef: RefObject<HTMLDivElement>
  onDrawn: (route: Route, startTime: Date, endTime: Date) => void
}

/** Today only, minimum fifteen minutes; the span pre-fills the new-stop dialog. */
export function useDragCreateStop({
  sortedRoutes,
  isToday,
  selectedDate,
  hourWidth,
  timelineContentRef,
  onDrawn,
}: DragCreateOptions) {
  const [dragCreateState, setDragCreateState] = useState<DragCreateState | null>(null)
  const [isCreatingStop, setIsCreatingStop] = useState(false)

  const pixelToTime = useCallback(
    (x: number) => positionToTime(x, selectedDate, hourWidth),
    [selectedDate, hourWidth]
  )

  const handleDragCreateStart = useCallback(
    (e: React.MouseEvent, rowIndex: number) => {
      if (e.button !== 0) return
      const route = sortedRoutes[rowIndex]
      if (!route || route.isLocked || !isToday) return
      const content = timelineContentRef.current
      if (!content) return
      const x = layoutOffsetX(content, e.clientX) + content.scrollLeft
      const startTime = snapToInterval(pixelToTime(x), SNAP_INTERVAL)
      setDragCreateState({
        rowIndex,
        startX: x,
        currentX: x,
        startTime,
        endTime: addMinutes(startTime, 15),
      })
      setIsCreatingStop(true)
    },
    [sortedRoutes, isToday, pixelToTime, timelineContentRef]
  )

  const handleDragCreateMove = useCallback(
    (e: MouseEvent) => {
      if (!dragCreateState || !isCreatingStop) return
      const content = timelineContentRef.current
      if (!content) return
      const x = layoutOffsetX(content, e.clientX) + content.scrollLeft
      const minWidthHours = 0.25
      const endTime = snapToInterval(
        pixelToTime(Math.max(x, dragCreateState.startX + hourWidth * minWidthHours)),
        SNAP_INTERVAL
      )
      setDragCreateState((prev) =>
        prev
          ? {
              ...prev,
              currentX: x,
              endTime:
                endTime.getTime() > prev.startTime.getTime()
                  ? endTime
                  : addMinutes(prev.startTime, 15),
            }
          : null
      )
    },
    [dragCreateState, isCreatingStop, pixelToTime, hourWidth, timelineContentRef]
  )

  const handleDragCreateEnd = useCallback(() => {
    if (!dragCreateState || !isCreatingStop) return
    const route = sortedRoutes[dragCreateState.rowIndex]
    if (route) {
      onDrawn(route, dragCreateState.startTime, dragCreateState.endTime)
    }
    setDragCreateState(null)
    setIsCreatingStop(false)
  }, [dragCreateState, isCreatingStop, sortedRoutes, onDrawn])

  useEffect(() => {
    if (!isCreatingStop) return
    window.addEventListener("mousemove", handleDragCreateMove)
    window.addEventListener("mouseup", handleDragCreateEnd)
    document.body.style.cursor = "crosshair"
    document.body.style.userSelect = "none"
    return () => {
      window.removeEventListener("mousemove", handleDragCreateMove)
      window.removeEventListener("mouseup", handleDragCreateEnd)
      document.body.style.cursor = ""
      document.body.style.userSelect = ""
    }
  }, [isCreatingStop, handleDragCreateMove, handleDragCreateEnd])

  return { dragCreateState, handleDragCreateStart }
}
